import { useIntl } from 'react-intl';

/**
 * Localizes the field-agnostic rule codes the shared Zod schemas emit. A
 * params-less rule arrives as the bare code ('required'); a parameterized one
 * arrives JSON-encoded ('{"code":"maxLength","max":255}') and carries its own
 * interpolation values.
 */

const KNOWN_RULES = new Set([
    'required',
    'maxLength',
    'dateFormat',
    'dateTimeFormat',
    'implausibleYear',
    'measureFormat',
    'scaleFormat',
    'numeric',
    'baseMeasureFormat',
    'digits',
    'unique',
    'duplicateNumber',
    'duplicatePart',
    'partRequired',
    'partConflict',
    'partLabelFormat',
    'duplicateFileName',
    'duplicateKey',
    'duplicateDate',
    'duplicateName',
    'duplicateTerm',
    'duplicateCode',
    'selfReference',
    'parentSkipped',
    'integer',
    'quotaRange',
    'quotaOverlap',
    'outsideQuota',
    'startAfterEnd',
    'endBeforeStart',
    'timeFormat',
    'pauseExceedsWork',
    'maxValue',
    'minValue',
    'future',
    'twoDecimals',
    'dateRangeMax',
    'outsideWeek',
    'duplicateAreaPlanum',
    'duplicateAreaPlanumSondage'
]);

export function useValidationMessage() {
    const intl = useIntl();

    return (raw: string | undefined): string => {
        if (!raw) {
            return intl.formatMessage({ id: 'validation.invalid' });
        }

        let code = raw;
        let values: Record<string, string | number> = {};
        try {
            const decoded = JSON.parse(raw) as {
                code?: string;
                [key: string]: unknown;
            };
            if (decoded && typeof decoded.code === 'string') {
                const { code: decodedCode, ...rest } = decoded;
                code = decodedCode;
                values = rest as Record<string, string | number>;
            }
        } catch {
            // Bare (params-less) code such as 'required' — not JSON, use as-is.
        }

        if (!KNOWN_RULES.has(code)) {
            return intl.formatMessage({ id: 'validation.invalid' });
        }
        return intl.formatMessage({ id: `validation.${code}` }, values);
    };
}
